import React, {useEffect, useState, useCallback} from 'react'
import {useDispatch, useSelector, batch} from 'react-redux'
import {Box, Grid, IconButton, Fab} from '@mui/material'
import {ArrowBackIosRounded as ArrowBackIosRoundedIcon} from '@mui/icons-material'
import {addDays, format} from 'date-fns'
import {
  BookingBookingPayload,
  BookingEmployeeBooking,
  BookingFeature,
  BookingSearchPayload,
  LabelFeature,
  OfficeFeature,
  ZoneFeature
} from '../../../services/booking/types'
import {BookingAvailability, ManagerBookingOptions} from '../consts'
import {BookingNewBooking, BookingNewBookingRange, PopoverProps} from '../Popover/types'
import {RootStore} from '../../../redux/store'
import {showErrorMessage, showInfoMessage} from '../../../redux/reducers/snackbarReducer'
import {FloorplanView} from './components'
import DeskPopover from '../Popover/Popover'
import {BookingZoom} from './BookingZoom'
import {
  setDeskBookingLoading,
  setDeskBookingShowMeBookingID,
  setDeskBookingShowGridView,
  setDeskBookingShowFloorplan,
  setDeskBookingInfinityBookingsResults
} from '../../../redux/reducers/deskBookingReducer'
import BookingErrorMessage from '../utils/BookingErrorMessage'
import {formatDatTimeWithTimeZone, formatTime} from '../../../utils/date-utils'
import {
  BookingFloorplanProps,
  BookingCancellationProps,
  BookingAdditionalInfoProps,
  BookingDynamicSVGCustomProps,
  BookingWeekdays
} from './types'
import {BookingFeatureTypes, BookingStatus, BookingZIndexLayers} from './enums'
import {
  combineDateAndTime,
  dateToNumber,
  doesBookingOverlapWithPeriod,
  getJson,
  hasOverlappingBookingForEmployee
} from '../utils/utils'
import {
  getBookingAvailabilityInPeriod,
  getColor,
  getDatesFromBookings,
  getPortAvailability,
  handleSingleBooking,
  initialPopoverProps,
  sortFeatures
} from './bookingFloorplanLogic'
import {setBookingWizardSelectedFeatureState} from '../../../redux/reducers/deskBookingWizardReducer'
import {BookingWizardSteps} from '../BlockBooking/enums'
import FullComponentLoadingIcon from '../../../shared/UI/LoadingIndicator/FullComponentLoadingIcon'
import {getDeskNameWithRow, handleSearch, handleServerError} from '../bookingLogic'
import {setDeskBookingSearchResults} from '../../../redux/reducers/deskBookingSearchReducer'
import {setSubmissionsInProgress} from '../../../redux/reducers/deskBookingStatusesReducer'
import {ErrorList} from '../../../utils/errorList'
import {bookingService} from '../../../services/booking/bookingService'
import actionsService from '../../../services/actionsService'
import {BaseResponse} from '../../../types/base-response'
import {BookingAvailabilityItem} from '../types'
import DeskRectangle from '../Desks/DeskRectangle'
import {normalizeKeys, pick} from '../Designer/Utils/toolbarUtils'
import {useTouchHandling} from './hooks/useTouchHandling'
import {useViewboxConfig} from './hooks/useViewboxConfig'
import {MouseState, BookingQueryParams, BookingTimeframe} from './types'
import { DEFAULT_MOUSE_STATE } from './consts'

export default function BookingFloorplanView({onCancel}: BookingFloorplanProps) {
    const {mobileDetection} = useSelector((state: RootStore) => state.responsiveDetection)
    const {isMobile, isMobileLayout} = mobileDetection

    // State management
    const [deskPoppoverProps, setDeskPoppoverProps] = useState<PopoverProps>(initialPopoverProps())
    const [zoom, setZoom] = useState<number>(0)
    const [viewboxX, setViewboxX] = useState<number>(0)
    const [viewboxY, setViewboxY] = useState<number>(0)
    const [mouseDown, setMouseDown] = useState<MouseState>(DEFAULT_MOUSE_STATE)
    const [loading, setLoading] = useState<boolean>(true)
    // Track window mouseup listener active
    const [isListeningWindowMouseUp, setIsListeningWindowMouseUp] = useState(false);
    const mouseUpListenerRef = React.useRef<(e: MouseEvent) => void>();
    useEffect(
        () => () => {
            if (mouseUpListenerRef.current) {
                window.removeEventListener("mouseup", mouseUpListenerRef.current);
            }
        },
        []
    );

  const {
    defaultViewBox,
    defaultZoom,
    touchConfig,
    svgViewBoxConfig,
  } = useViewboxConfig({
    mobileDetection,
    viewboxState: { x: viewboxX, y: viewboxY, zoom },
  })

  useEffect(() => {
    setZoom(defaultZoom)
  }, [defaultZoom])

  const handleViewboxChange = useCallback((newX: number, newY: number) => {
    setViewboxX(newX)
    setViewboxY(newY)
  }, [])

  const handleZoomChange = useCallback((scaleChange: number) => {
    setZoom(prevScale => prevScale * scaleChange)
  }, [])

  const {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  } = useTouchHandling({
    dragFactor: touchConfig.dragFactor,
    onViewboxChange: handleViewboxChange,
    onZoomChange: handleZoomChange,
    currentViewbox: { x: viewboxX, y: viewboxY }
  })

  const {
    locations,
    floorplans,
    openFloorplan,
    zones,
    showMeBookingID,
    floorplanViewingDate,
    featuresForFloorPlan,
    focussedZoneID,
    dashboardResults,
    bookingIsLoading,
    bookingSliderPosition,
    floorplanManuallySelected,
    foundDeskFeatureId,
    foundDeskTimestamp
  } = useSelector((state: RootStore) => state.deskBooking)
  const {searchResults, searchParams} = useSelector((state: RootStore) => state.deskBookingSearch)
  const {submissionsInProgress} = useSelector((state: RootStore) => state.deskBookingStatus)
  const userPermissions = useSelector<RootStore, string[]>(
    (state: RootStore) => state.userState.permissions
  )
  const {employeeDetails} = useSelector((state: RootStore) => state.appSettings)
  
  // Determine if manager is booking for someone else (employee or visitor)
  const isManager = userPermissions.includes('BookingManager')
  const searchEmployeeId = searchParams?.employeeID
  const searchBookingFor = searchParams?.bookingFor
  
  // Check if manager is booking for someone other than themselves
  const isManagerBookingForOther = isManager && searchBookingFor && searchBookingFor !== ManagerBookingOptions.MYSELF
  
  // Determine booking type and get details
  const getBookingDetails = () => {
    if (!isManagerBookingForOther || !searchParams) {
      return {
        bookingFor: undefined,
        selectedEmployee: undefined
      }
    }
    
    if (searchBookingFor === ManagerBookingOptions.EMPLOYEE && searchEmployeeId) {
      return {
        bookingFor: ManagerBookingOptions.EMPLOYEE,
        selectedEmployee: {
          value: searchEmployeeId,
          displayValue: searchParams.employeeName || `Employee ${searchEmployeeId}`
        }
      }
    }
    
    if (searchBookingFor === ManagerBookingOptions.VISITOR) {
      return {
        bookingFor: ManagerBookingOptions.VISITOR,
        selectedEmployee: {
          value: -1, // Visitor placeholder ID
          displayValue: searchParams.employeeName || 'Visitor'
        }
      }
    }
    
    return {
      bookingFor: undefined,
      selectedEmployee: undefined
    }
  }
  
  const { bookingFor: controlledBookingFor, selectedEmployee: controlledSelectedEmployee } = getBookingDetails()
  const userInfo = useSelector((state: RootStore) => state.userState.loggedInUser)
  const {
    existingBookings,
    weekdaysSelected,
    currentStep: wizardCurrentStep,
    selectedDates
  } = useSelector((state: RootStore) => state.deskBookingWizard)

  const dispatch = useDispatch()

  const establishCurrentBookings = (featureId: number) => {
    if (wizardCurrentStep === BookingWizardSteps.STEP_0_INACTIVE) {
      if (floorplanManuallySelected) {
        return searchResults.filter(
          f =>
            f.featureId === featureId &&
            (f.statusId === 1 || f.statusId === 2) &&
            (dateToNumber(f.fromDate) === dateToNumber(floorplanViewingDate)
              || dateToNumber(f.toDate) === dateToNumber(floorplanViewingDate)
            )
        )
      }

      return searchResults?.filter(
        f =>
          f.featureId === featureId &&
          (f.statusId === 1 || f.statusId === 2) &&
          searchParams &&
          doesBookingOverlapWithPeriod(f, floorplanViewingDate, searchParams.from, searchParams.to)
      )
    }
    if (wizardCurrentStep === BookingWizardSteps.STEP_4_ALTERNATIVES) {
      return existingBookings.filter(
        f =>
          f.featureId === featureId &&
          dateToNumber(f.fromDate) === dateToNumber(floorplanViewingDate)
      )
    }
    return existingBookings.filter(f => f.featureId === featureId)
  }

  const findIfBookingsExistForEmployee = (queryParams: BookingQueryParams) => {
    if (!searchParams || !dashboardResults?.results) {
      return false
    }

    // Get all employee bookings (convert BookingEmployeeBooking to BookingBookingPayload format)
    const employeeBookings: BookingBookingPayload[] = dashboardResults.results
      .filter(booking => booking.employeeId === queryParams.employeeId)
      .map(booking => ({
        id: booking.id,
        employeeId: booking.employeeId,
        employeeName: booking.displayName,
        displayName: booking.displayName,
        floorPlanId: booking.feature.floorPlanId,
        featureId: booking.feature.id,
        fromDate: booking.fromDate,
        fromTime: booking.fromTime,
        toDate: booking.toDate,
        toTime: booking.toTime,
        createdByDate: '',
        statusId: booking.statusId,
        status: 'Accepted',
        deskName: booking.feature.label || '',
        featureType: {
          id: booking.feature.id,
          label: booking.feature.label
        }
      }))

  const allBookings = [...employeeBookings, ...searchResults]
    
    return hasOverlappingBookingForEmployee(
      queryParams.employeeId,
      searchParams.date,
      searchParams.from,
      searchParams.to,
      allBookings
    )
  }

  const featureBookingWithinTimeframe = (
    featureId: number,
    bookings: BookingBookingPayload[] | undefined
  ) => bookings && bookings.length > 0 ? bookings : establishCurrentBookings(featureId)

  const resetPopoverProps = () => {
    setDeskPoppoverProps((current) => ({...current, open: false}))
  }

  const applyPoppoverData = (
    e: SVGElement,
    floorplan: string,
    featureDetail: BookingFeature,
    isOwnBooking: boolean,
    isBlockBooking: boolean,
    availability: BookingAvailabilityItem
  ) => {
    if (!searchResults || !searchParams) {
      return
    }

    const featureId = featureDetail.id
    const featureIdIsAvailable = !searchResults.some((f) => f.featureId === featureId)

    // Only validate overlapping bookings for non-managers booking for themselves
    // Managers can book multiple desks (for themselves or others)
    const shouldValidateOverlap = !isBlockBooking &&
                                featureIdIsAvailable &&
                                !isManager

    if (shouldValidateOverlap  && findIfBookingsExistForEmployee({ featureId, employeeId: employeeDetails.employeeId })) {
      const alertObj = ErrorList.find((err) => err.name === 'DeskAlreadyBookedForTheDay')
      dispatch(showInfoMessage(alertObj!.message))
      return
    }

    setDeskPoppoverProps({
      svgElement: e,
      open: true,
      floorplan,
      bookings: establishCurrentBookings(featureId),
      dateTimeFrom: formatDatTimeWithTimeZone(`${searchParams.date} ${searchParams.from}`),
      dateTimeTo: formatDatTimeWithTimeZone(`${searchParams.date} ${searchParams.to}`),
      featureDetail,
      isOwnBooking,
      isBlockBooking,
      isManager,
      availability
    })
  }

  useEffect(() => {
    if (!showMeBookingID || showMeBookingID === undefined) {
      return
    }

    setTimeout(() => dispatch(setDeskBookingShowMeBookingID(undefined)), 2000)
  }, [dispatch, showMeBookingID])

  useEffect(() => {
    if (!featuresForFloorPlan || !searchParams) {
      return
    }

    // Don't auto-scroll to top if we're zooming to a found desk
    if (foundDeskTimestamp) {
      return
    }
    if (focussedZoneID) {
      return
    }

    setTimeout(() => {
      const heighestSVG = featuresForFloorPlan.sort((a, b) => {
        if (a.y < b.y) {
          return -1
        }
        if (a.y > b.y) {
          return 1
        }
        return 0
      })
      setViewboxY(heighestSVG[0].y + 200)
    }, 300)
  }, [featuresForFloorPlan, foundDeskTimestamp, focussedZoneID])

  useEffect(() => {
    resetPopoverProps()
  }, [])

  // Effect to handle auto-zoom and pan to found desk
  useEffect(() => {
    if (!foundDeskFeatureId || !foundDeskTimestamp || !featuresForFloorPlan) {
      return
    }

    // Find the desk feature in the features array
    const deskFeature = featuresForFloorPlan.find(f => f.id === foundDeskFeatureId)
    
    if (!deskFeature) {
      return
    }

    // Calculate the center point of the desk
    const deskCenterX = deskFeature.x + deskFeature.width / 2
    const deskCenterY = deskFeature.y + deskFeature.height / 2

    // Set zoom level to focus on the desk
    const targetZoom = isMobileLayout ? 300 : 200

    // Calculate viewbox dimensions for focused view
    // We want a small viewbox centered on the desk
    const viewboxWidth = isMobileLayout ? 600 : 800  // Fixed width for focused view
    const viewboxHeight = isMobileLayout ? 450 : 600  // Fixed height for focused view
    
    const targetViewboxX = deskCenterX - viewboxWidth / 2
    const targetViewboxY = deskCenterY - viewboxHeight / 2

    // Animate the transition smoothly
    setZoom(targetZoom)
    setViewboxX(targetViewboxX)
    setViewboxY(targetViewboxY)
  }, [foundDeskFeatureId, foundDeskTimestamp, featuresForFloorPlan, isMobileLayout, defaultViewBox.width, defaultViewBox.height])

  const loadingCheckOnce = useCallback(() => {
    setLoading(true)

    setTimeout(() => {
      if (searchResults !== undefined && !bookingIsLoading && dashboardResults?.results) {
        setLoading(false)
      }
    }, 300)
  }, [bookingIsLoading, searchResults, dashboardResults?.results])

  useEffect(() => {
    resetPopoverProps()

    loadingCheckOnce()
  }, [searchResults.length, dashboardResults?.results, bookingIsLoading, loadingCheckOnce])

  useEffect(() => {
    if (zones.length === 0 || !featuresForFloorPlan) {
      return
    }

    setTimeout(() => {
      if (featuresForFloorPlan.length === 0) {
        return
      }

      const zone = zones.find((f) => f.id === focussedZoneID)

      if (zone?.additionalInfo) {
        const json = getJson(zone?.additionalInfo)

        const svgElement = json.svg[0]
        let zoneX: number | undefined
        let zoneY: number | undefined

        // Handle rect-type zones (v1 floor plans)
        if (svgElement.x !== undefined && svgElement.y !== undefined) {
          zoneX = svgElement.x
          zoneY = svgElement.y
        }
        // Handle polygon-type zones (v2 floor plans)
        else if (svgElement.type === 'polygon' && svgElement.points && Array.isArray(svgElement.points)) {
          // Calculate min X and Y from polygon points
          const points = svgElement.points as [number, number][]
          zoneX = Math.min(...points.map(p => p[0]))
          zoneY = Math.min(...points.map(p => p[1]))
        }

        if (zoneX !== undefined && zoneY !== undefined) {
          const newViewboxX = zoneX - 15
          const newViewboxY = zoneY - 210
          setViewboxX(newViewboxX)
          setViewboxY(newViewboxY)
          return
        }
      }

      // if the floor plan has no zones, or zone has no coordinates, snap to the top left corner of the floor map
      const minYCoordinate = featuresForFloorPlan.sort((a, b) => {
        if (a.y < b.y) {
          return -1
        }
        if (a.y > b.y) {
          return 1
        }
        return 0
      })[0].y

      const minXCoordinate = featuresForFloorPlan.sort((a, b) => {
        if (a.x < b.x) {
          return -1
        }
        if (a.x > b.x) {
          return 1
        }
        return 0
      })[0].x

      setViewboxY(minYCoordinate)
      setViewboxX(minXCoordinate)
    }, 13)
  }, [zones, focussedZoneID, featuresForFloorPlan])

  const handleWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    if (e.shiftKey) {
      if (e.deltaY > 0) {
        setViewboxX(viewboxX + e.screenX / 100)
      } else {
        setViewboxX(viewboxX - e.screenX / 100)
      }
      return
    }
    if (e.altKey) {
      if (e.deltaY > 0) {
        setViewboxY(viewboxY + e.screenX / 100)
      } else {
        setViewboxY(viewboxY - e.screenX / 100)
      }
      return
    }
    // deltaY is the amount of pixels that is moved in a vertical direction.
    // If you push the wheel forward, it's a negative number (-100) otherwise its a positive number (100).
    if (zoom + e.deltaY < -40) {
      // We have reached the largest zoom threshold allowed, so don't zoom in any more.
      return
    }
    setZoom(zoom + e.deltaY)
    // console.log('tom- handleWheel zoom', zoom)
  }

  const moreOrless = (more: number, than: number, factor: number) =>
    more + factor > than || more < than + factor

  const renderRect = (feature: Partial<BookingFeature>, isZone: boolean) => {
    let colourFill = getColor(feature as BookingFeature)

    if (openFloorplan) {
      const featureWidth = feature?.width
      const featureZone = feature?.zone
      const featureZoneName = featureZone?.name
      const featureZoneId = featureZone?.id

      const isSmallFeatureWithNonNumericLabelAndZone = featureZoneName && feature.label === featureZoneName && featureZoneName?.match(/[A-Za-z]+/g)
      const isSmallFeatureLabelWithinZone = featureZoneId && (featureWidth && featureWidth > 50 || isSmallFeatureWithNonNumericLabelAndZone)
      const isSmalLFeatureLabelInOpenArea = !featureZoneId && featureZone?.additionalInfo === ''

      const featureFillIsZoneColourBadge = feature.fill === 'zone.colors.badge'

      if (isZone) return null
      if (featureFillIsZoneColourBadge &&
        (isSmallFeatureLabelWithinZone || isSmalLFeatureLabelInOpenArea)) return null
      if (featureFillIsZoneColourBadge) colourFill = '#B2B2B2'
    }

    return (
      <g id={String(feature?.id || 0)}>
        <rect
          id={`rect_${feature?.id}`}
          x={feature.x}
          y={feature.y}
          width={feature.width}
          height={feature.height}
          rx={feature.borderRadius}
          fill={colourFill}
        />
      </g>
    )
  }

  const renderOffice = (feature: Partial<BookingFeature>) => {
    const colourFill = getColor(feature as BookingFeature)

    const labelX = feature.x! + (feature.width! / 2)
    const labelY = feature.y! + (feature.height! / 2)
    const textAnchor = 'middle'

    return (
      <g id={String(feature?.id || 0)}>
        <rect
          id={`rect_${feature?.id}`}
          x={feature.x}
          y={feature.y}
          width={feature.width}
          height={feature.height}
          rx={feature.borderRadius}
          fill={colourFill}
        />
        {feature.label && (
          <text
            x={labelX}
            y={labelY}
            textAnchor={textAnchor}
            dominantBaseline="middle"
            fill="white"
            fontSize="22px"
            fontFamily="Poppins"
          >
            {feature.label}
          </text>
        )}
      </g>
    )
  }

  const renderText = (feature: BookingFeature) => {
    let parsedJson: BookingAdditionalInfoProps | null = null
    try {
      parsedJson = getJson(feature.additionalInfo)
      if (!parsedJson) {
        return null
      }
    } catch (e) {
      return null
    }
    const fontStyle = parsedJson?.fontStyle
    return (
      <text
        id={`text_${feature?.id}`}
        x={feature.x}
        y={feature.y}
        fontFamily={fontStyle?.fontFamily}
        fontSize={fontStyle?.fontSize}
        fill={feature.fill}
        style={{userSelect: 'none'}}
      >
        {feature.label}
      </text>
    )
  }

  const renderLabel = (feature: LabelFeature) => {
    const additionalInfo = JSON.parse(feature.additionalInfo)
    const normalizedInfo = normalizeKeys(additionalInfo)
    Object.assign(feature, pick(normalizedInfo, ['color', 'fontSize', 'chip']))

    const fontSize = `${feature.fontSize}px`
    const defaultFontFamily = 'Poppins'
    const defaultFontSize = '16px'
    const chip = feature.chip || false

    // Create a temporary SVG element to measure the text dimensions
    const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    const tempText = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    tempText.setAttribute('font-family', defaultFontFamily)
    tempText.setAttribute('font-size', fontSize || defaultFontSize)
    tempText.textContent = feature.label
    tempSvg.appendChild(tempText)
    document.body.appendChild(tempSvg)
    const textWidth = tempText.getBBox().width
    const textHeight = tempText.getBBox().height
    document.body.removeChild(tempSvg)
    const zoneFill = feature.fill === 'zone.colors.badge' ? '#20C5A0' : '#e0e0e0'
    const textColour = feature.fill === 'zone.colors.badge' ? '#fff' : feature.fill
    return (
      <g>
        {chip && (
          <rect
            x={feature.x - textWidth / 2 - 10}
            y={feature.y - textHeight / 2 - 5}
            width={textWidth + 20}
            height={textHeight + 10}
            fill={zoneFill}
            rx={5}
            ry={5}
          />
        )}
        <text
          id={`text_${feature?.id}`}
          x={feature.x}
          y={feature.y}
          fontFamily={defaultFontFamily}
          fontSize={fontSize || defaultFontSize}
          fill={textColour}
          style={{userSelect: 'none'}}
          textAnchor="middle"
          alignmentBaseline="middle"
        >
          {feature.label}
        </text>
      </g>
    )
  }

  const renderSVGLine = (lineData: BookingDynamicSVGCustomProps) => {
    const strokeWidth = parseFloat(lineData.style?.strokeWidth ?? '1')
    const strokeLinecap = strokeWidth > 10 ? 'butt' : 'round'

    return (
      <line
        x1={lineData.x1}
        y1={lineData.y1}
        x2={lineData.x2}
        y2={lineData.y2}
        style={{
          stroke: lineData.style?.stroke,
          strokeWidth: strokeWidth.toString(),
          strokeLinecap
        }}
        strokeDasharray={lineData.strokeDasharray}
      />
    )
  }

  const renderSVGCustom = (feature: BookingFeature) => {
    switch (feature.typeId) {
    case BookingFeatureTypes.Wall: {
      if (feature.additionalInfo !== '') {
        const additionalInfo = JSON.parse(feature.additionalInfo)
        const normalizedInfo = normalizeKeys(additionalInfo)
        Object.assign(feature, pick(normalizedInfo, ['startPoint', 'endPoint', 'thickness']))
      }
      return renderSVGLine({
        type: 'line',
        x1: feature.startPoint.x,
        y1: feature.startPoint.y,
        x2: feature.endPoint.x,
        y2: feature.endPoint.y,
        style: {
          stroke: feature.fill,
          strokeWidth: (parseFloat(feature.thickness?.toString() || '1') + 1).toString() // Increase stroke width slightly
        }
      })
    }
    default:
      return null
    }
  }

  const getRoundedPolygonPath = (points: [number, number][], radius: number): string => {
    let d = ''
    const len = points.length

    for (let i = 0; i < len; i += 1) {
      const currPoint = points[i]
      const prevPoint = points[(i - 1 + len) % len]
      const nextPoint = points[(i + 1) % len]

      // Calculate the vectors
      const vectorA = [currPoint[0] - prevPoint[0], currPoint[1] - prevPoint[1]]
      const vectorB = [nextPoint[0] - currPoint[0], nextPoint[1] - currPoint[1]]

      // Normalize the vectors
      const lengthA = Math.hypot(vectorA[0], vectorA[1])
      const lengthB = Math.hypot(vectorB[0], vectorB[1])
      const unitVectorA = [vectorA[0] / lengthA, vectorA[1] / lengthA]
      const unitVectorB = [vectorB[0] / lengthB, vectorB[1] / lengthB]

      // Calculate the maximum radius allowed for the corner
      const angle = Math.acos(
        (unitVectorA[0] * unitVectorB[0] + unitVectorA[1] * unitVectorB[1]) * -1
      )
      const maxRadius = Math.min(
        (Math.tan(angle / 2) * Math.min(lengthA, lengthB)) / 2,
        radius
      )
      const adjustedRadius = Number.isNaN(maxRadius) ? radius : maxRadius

      // Calculate the points for the lines that will be connected by the arcs
      const innerPoint1 = [
        currPoint[0] - unitVectorA[0] * adjustedRadius,
        currPoint[1] - unitVectorA[1] * adjustedRadius
      ]
      const innerPoint2 = [
        currPoint[0] + unitVectorB[0] * adjustedRadius,
        currPoint[1] + unitVectorB[1] * adjustedRadius
      ]

      if (i === 0) {
        // Move to the first point
        d += `M${innerPoint1[0]},${innerPoint1[1]}`
      } else {
        // Draw a line to the current point
        d += `L${innerPoint1[0]},${innerPoint1[1]}`
      }

      // Draw the arc between the lines
      d += `Q${currPoint[0]},${currPoint[1]} ${innerPoint2[0]},${innerPoint2[1]}`
    }

    d += 'Z' // Close the path

    return d
  }

  const renderZone = (feature: ZoneFeature) => {
    // Parse the additionalInfo to extract SVG data
    let additionalInfo: { svg: any[]; colors?: { badge?: string } }
    try {
      additionalInfo = JSON.parse(feature.additionalInfo)
    } catch (e) {
      console.error('Failed to parse additionalInfo for ZoneFeature', e)
      return null
    }

    const {svg, colors} = additionalInfo

    if (!svg || !Array.isArray(svg) || svg.length === 0) {
      console.error('No valid SVG data in additionalInfo for ZoneFeature')
      return null
    }

    // Determine the fill color for the zone
    const fillColor = feature.fill || '#F2F2F2'

    // Assuming only one element in svg array for simplicity
    const svgElement = svg[0]

    if (svgElement.type !== 'polygon') {
      console.error('Unsupported SVG type in ZoneFeature')
      return null
    }

    const polygonPoints = svgElement.points as [number, number][]

    // Set the radius for rounded corners
    const cornerRadius = 20 // Adjust the radius as needed

    // Get the path data with rounded corners
    const pathData = getRoundedPolygonPath(polygonPoints, cornerRadius)

    return (
      <g id={`zone_${feature.id}`}>
        <path
          d={pathData}
          fill={fillColor}
          stroke="#000"
          strokeWidth="1"
          opacity="0.5"
        />
      </g>
    )
  }

  const renderDesk = (feature: BookingFeature) => {
    if (!searchParams) {
      return null; // Return null if searchParams is undefined
    }

    const rangeFrom = searchParams.from;
    const rangeTo = searchParams.to;
    const { date } = searchParams;
    const bookings =
      wizardCurrentStep === BookingWizardSteps.STEP_0_INACTIVE
        ? searchResults?.filter(
            f => f.featureId === feature.id
              && doesBookingOverlapWithPeriod(f, date, rangeFrom, rangeTo)
          )
        : [];
  
    return (
      <DeskRectangle
        featureDetail={feature}
        isActive={feature.zone?.id === focussedZoneID}
        availability={
          !openFloorplan &&
          ((feature.zone?.id !== searchParams.zoneId &&
              feature.zone?.id !== focussedZoneID) ||
            feature.zone?.id !== focussedZoneID)
            ? BookingAvailability.NOT_ACTIVE
            : getPortAvailability(
              wizardCurrentStep === BookingWizardSteps.STEP_2_SELECT_DESK
                ? selectedDates.map(m => m.date)
                : [
                  floorplanViewingDate ||
                  searchParams?.date ||
                  new Date()
                ],
              datesFromBookings()
                .filter(
                  f => f.featureId === feature.id
                ),
              {start: searchParams.from, end: searchParams.to}
            )
        }
        existingBookings={featureBookingWithinTimeframe(
          feature.id,
          bookings
        )}
        isBlockBooking={isBlockBooking}
        onActionMouseEnter={(
          e,
          availability,
          isOwnBooking,
          _bookings
        ) => {
          if (submissionsInProgress.some(s => s === feature.id)) {
            return resetPopoverProps();
          }
          if (mouseDown.down && Date.now() - mouseDown.at > 1000) {
            return;
          }
          if (
            feature !== deskPoppoverProps.featureDetail &&
            deskPoppoverProps.open
          ) {
            return resetPopoverProps();
          }
          
          applyPoppoverData(
            e,
            floorplans.find(f => f.id === feature.floorPlanId)?.name || '',
            feature,
            isOwnBooking,
            isBlockBooking,
            availability
          );
        }}
      />
    );
  };

  const renderSVGType = (
    feature: string | BookingFeature,
    isZone: boolean
  ): (JSX.Element | null)[] => {
    switch ((feature as BookingFeature).typeId) {
      case BookingFeatureTypes.Desk:
        return [renderDesk(feature as BookingFeature)]
      case BookingFeatureTypes.SVG_Rectangle:
        return [renderRect(feature as BookingFeature, isZone)]
      case BookingFeatureTypes.SVG_Text:
        return [renderText(feature as BookingFeature)]
      case BookingFeatureTypes.Wall:
        return [renderSVGCustom(feature as BookingFeature)]
      case BookingFeatureTypes.Zone:
        return [renderZone(feature as ZoneFeature)]
      case BookingFeatureTypes.Label:
        return [renderLabel(feature as LabelFeature)]
      case BookingFeatureTypes.OfficeOrMeeting:
        return [renderOffice(feature as OfficeFeature)]
      default:
        return [null]
    }
  }

  const datesFromBookings = useCallback(() => {
    let localBookings: BookingBookingPayload[]
    let localWeekdays: BookingWeekdays[] | undefined
    if (wizardCurrentStep === BookingWizardSteps.STEP_0_INACTIVE) {
      localBookings = searchResults || []
    } else {
      localBookings = existingBookings || []
      localWeekdays = weekdaysSelected
    }
    return getDatesFromBookings(localBookings, localWeekdays)
  }, [wizardCurrentStep, searchResults, existingBookings, weekdaysSelected])

  if (!locations || !floorplans || !zones || !searchParams) {
    return null
  }

  const isBlockBooking = wizardCurrentStep !== BookingWizardSteps.STEP_0_INACTIVE
  const isFloorPlanView = bookingSliderPosition.buttonClick || floorplanManuallySelected
  const bookingZoomTopSingleFloorPlan = isFloorPlanView ? 5 : -20
  const bookingZoomTop = isBlockBooking ? -69 : bookingZoomTopSingleFloorPlan

  const controller = new AbortController()

  const handleSearchError = (err: string) => {
    dispatch(showErrorMessage(<BookingErrorMessage name={err} />))
  }

  const getByQuerySearchResults = (
    floorplanId: number,
    date: Date,
    selectedRange: BookingNewBookingRange,
    dateTo?: Date
  ) => {
    const selectedDate = format(date, 'yyyy-MM-dd')
    const dateToFormatted = dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined

    return bookingService
      .search(controller, {
        floorplanId,
        date: selectedDate,
        ...(deskPoppoverProps.dateTimeTo && {
          selectedDate
        }),
        from: formatTime(selectedRange.from),
        to: formatTime(selectedRange.to),
        dateTo: dateToFormatted
      })
      .then((result) => result)
      .catch((err) => {
        const response: BaseResponse = handleServerError(err.response.data)
        if (response.status === 500) {
          dispatch(showErrorMessage(`Search Floorplan ${floorplanId} bookings failure`))
        }
        response.errors.forEach((error) => {
          handleSearchError(error.name)
        })

        dispatch(setDeskBookingLoading(false))
      })
  }

  const isAlreadyBooked = (result: BookingSearchPayload, featureId: number, newBooking: BookingNewBooking) => {
    const featureBookings = result.bookings.filter(
      (x: BookingBookingPayload) =>
        x.featureId === featureId && x.statusId === BookingStatus.ACCEPTED
    )
    const startDate = combineDateAndTime(newBooking.date, newBooking.selectedRange.from)
    const endDate = combineDateAndTime(newBooking.date, newBooking.selectedRange.to)
    const availability = getBookingAvailabilityInPeriod(startDate, endDate, featureBookings)
    return availability !== BookingAvailability.AVAILABLE
  }

  const {BOOKING_MOBILE_FLOORPLAN_VIEW_OVERLAY, BOOKING_MOBILE_FLOORPLAN_VIEW_BACK_BUTTON} =
    BookingZIndexLayers

  return (
    <FloorplanView container id="floorplan">
      {isMobile && isMobileLayout && deskPoppoverProps.open && (
        <Box
          style={{
            position: 'fixed',
            top: '0px',
            left: '0px',
            width: '100%',
            height: '100%',
            background:
              'linear-gradient(180deg, rgba(170,163,163,0.5144192122186495) 0%, rgba(74,74,74,0.8552552250803859) 64%)',
            opacity: 0.7,
            zIndex: BOOKING_MOBILE_FLOORPLAN_VIEW_OVERLAY,
          }}
          onClick={() => resetPopoverProps()}
        />
      )}
      {isMobileLayout && (
        <Fab
          style={{
            position: 'fixed',
            top: '80px',
            left: '20px',
            background: 'white',
            color: '#747474',
            opacity: 0.9,
            zIndex: BOOKING_MOBILE_FLOORPLAN_VIEW_BACK_BUTTON,
          }}
          size="small"
          color="secondary"
          aria-label="Back to results"
        >
          <IconButton
            color="default"
            size="small"
            onClick={() => {
              actionsService.userAnalytics.postEvent(
                'UIClickEvent',
                'BookingFloorplanBackButtonClick',
                window.location.pathname,
                window.location.hostname
              )
              batch(() => {
                dispatch(setDeskBookingInfinityBookingsResults([]))
                dispatch(setDeskBookingShowFloorplan(false))
                dispatch(setDeskBookingShowGridView(true))
              })
            }}
          >
            <ArrowBackIosRoundedIcon color="primary" />
          </IconButton>
        </Fab>
      )}
      <FullComponentLoadingIcon
        loading={loading}
        noData={!searchResults || !floorplans}
        bgColor="transparent"
      >
        <Grid item xs={12}>
          {!isMobileLayout && (
            <BookingZoom
              top={bookingZoomTop}
              factor={250}
              onZoom={factor => {
                setZoom(zoom - factor)
                // console.log('tom- BookingZoom', zoom)
              }}
            />
          )}
          <Box
            width="auto"
            height="100%"
            style={{
              opacity: loading ? 0.25 : 1,
              cursor: mouseDown.down ? 'grab' : 'move',
              userSelect: 'none',
            }}
          >
            {Boolean(featuresForFloorPlan?.length) && (
              <>
                {deskPoppoverProps.open && (
                  <DeskPopover
                    svgElement={deskPoppoverProps.svgElement}
                    open={deskPoppoverProps.open}
                    featureDetail={deskPoppoverProps.featureDetail}
                    bookings={deskPoppoverProps.bookings}
                    isOwnBooking={deskPoppoverProps.isOwnBooking}
                    isBlockBooking={deskPoppoverProps.isBlockBooking}
                    isManager={deskPoppoverProps.isManager}
                    floorplan={deskPoppoverProps.floorplan}
                    dateTimeFrom={deskPoppoverProps.dateTimeFrom}
                    dateTimeTo={deskPoppoverProps.dateTimeTo}
                    availability={deskPoppoverProps.availability}
                    bookingFor={controlledBookingFor}
                    selectedEmployee={controlledSelectedEmployee}
                    onClose={resetPopoverProps}
                    onSubmit={async (
                      feature,
                      deskName,
                      { selectedRange, date, employeeId, employeeName, bookingFor }
                    ) => {
                      dispatch(setSubmissionsInProgress([...submissionsInProgress, feature.id]))
                      resetPopoverProps()
                      if (!searchResults || !userInfo?.name) {
                        return
                      }

                      // Use the selected employee ID if provided, otherwise use current user's employee ID
                      const bookingEmployeeId = employeeId || employeeDetails.employeeId
                      const bookingEmployeeName = employeeName || userInfo?.name

                      const searchResultsResponse = await getByQuerySearchResults(
                        feature.floorPlanId,
                        date,
                        selectedRange,
                        addDays(date, 1)
                      )

                      if (
                        searchResultsResponse &&
                        isAlreadyBooked(searchResultsResponse, feature.id, { date, selectedRange })
                      ) {
                        const alertObj = ErrorList.find(
                          err => err.name === 'OtherAlreadyHasDeskBookedForTheDay'
                        )
                        dispatch(showErrorMessage(alertObj!.message))
                        dispatch(
                          setDeskBookingSearchResults([
                            ...searchResults.filter(f => f.statusId === BookingStatus.ACCEPTED),
                            ...searchResultsResponse.bookings.filter(
                              f => f.statusId === BookingStatus.ACCEPTED
                            ),
                          ])
                        )
                        return
                      }

                      await handleSingleBooking(
                        feature,
                        getDeskNameWithRow(feature),
                        { selectedRange, date },
                        locations,
                        floorplans,
                        bookingEmployeeId,
                        bookingFor,
                        bookingEmployeeName,
                        floorplanViewingDate,
                        dispatch,
                        err => {
                          dispatch(showErrorMessage(<BookingErrorMessage name={err} />))
                        },
                        submissionsInProgress
                      ).then(async () => {
                        if (isMobile) {
                          const searchResultsResponseNew = await getByQuerySearchResults(
                            feature.floorPlanId,
                            date,
                            selectedRange,
                            addDays(date, 1)
                          )

                          if (searchResultsResponseNew) {
                            dispatch(
                              setDeskBookingSearchResults([
                                ...searchResults.filter(f => f.statusId === BookingStatus.ACCEPTED),
                                ...searchResultsResponseNew.bookings.filter(
                                  f => f.statusId === BookingStatus.ACCEPTED
                                ),
                              ])
                            )
                          }
                        }
                      })
                    }}
                    onCancel={({
                      bookingID,
                      deskName,
                      floorPlanName,
                      location,
                      onCallBack,
                      byManager,
                    }: BookingCancellationProps) => {
                      if (!searchResults) {
                        return
                      }
                      onCancel?.({
                        bookingID,
                        deskName,
                        floorPlanName,
                        location,
                        byManager,
                        date: floorplanViewingDate,
                        onCallBack: success => {
                          dispatch(
                            setDeskBookingSearchResults(
                              searchResults.filter(f => f.id !== bookingID)
                            )
                          )
                          resetPopoverProps()
                          onCallBack(success)
                        },
                      })
                    }}
                  />
                )}
                <svg
                  key={`floorplan-${searchParams?.floorplanId}-${featuresForFloorPlan?.[0]?.floorPlanId}`}
                  width="100%"
                  height="100%"
                  style={{ touchAction: 'none' }}
                  viewBox={svgViewBoxConfig}
                  onMouseDown={e => {
                    e.preventDefault()
                    setMouseDown({
                      down: true,
                      at: Date.now(),
                      pageX: viewboxX + e.pageX,
                      pageY: viewboxY + e.pageY,
                    })
                    // Attach window mouseup listener (once per drag start)
                    if (!isListeningWindowMouseUp) {
                      const onWindowMouseUp = (ev: MouseEvent) => {
                        setMouseDown(current => ({ ...current, down: false }))
                        setIsListeningWindowMouseUp(false)
                        window.removeEventListener('mouseup', onWindowMouseUp)
                      }
                      mouseUpListenerRef.current = onWindowMouseUp
                      window.addEventListener('mouseup', onWindowMouseUp)
                      setIsListeningWindowMouseUp(true)
                    }

                    if (
                      isBlockBooking &&
                      wizardCurrentStep !== BookingWizardSteps.STEP_4_ALTERNATIVES
                    ) {
                      return dispatch(setBookingWizardSelectedFeatureState(undefined))
                    }
                  }}
                  onMouseUp={e => {
                    e.preventDefault()
                    setMouseDown(current => ({ ...current, down: false }))
                  }}
                  onMouseMove={e => {
                    if (mouseDown.down) {
                      e.preventDefault()
                      setViewboxX(mouseDown.pageX - e.pageX)
                      setViewboxY(mouseDown.pageY - e.pageY)
                    }
                  }}
                  onWheel={handleWheel}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {searchResults &&
                    searchParams &&
                    zones
                      .filter(f => f.floorPlanId === searchParams.floorplanId)
                      .map(m => {
                        let json
                        try {
                          json = getJson(m.additionalInfo)
                        } catch (err) {
                          /* empty */
                        }
                        if (json && json.svg && json.svg.length > 0) {
                          return (json.svg as Partial<BookingFeature>[]).map(ai => {
                            if (openFloorplan && ai.fill === 'zone.colors.badge') return null
                            return renderRect(
                              {
                                id: m.id,
                                x: ai.x || 0,
                                y: ai.y || 0,
                                width: ai.width || 0,
                                height: ai.height || 0,
                                borderRadius: ai.borderRadius || 0,
                                fill: ai.fill || '0',
                              },
                              true
                            )
                          })
                        }
                        return renderSVGType(m.additionalInfo, true)
                      })}

                  {featuresForFloorPlan &&
                    sortFeatures(featuresForFloorPlan).map(feature =>
                      renderSVGType(feature, false)
                    )}
                </svg>
              </>
            )}
          </Box>
        </Grid>
      </FullComponentLoadingIcon>
    </FloorplanView>
  )
}

// (Removed: invalid top-level useEffect)
